import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { 
  Truck, Clock, MapPin, User, CloudSun, Navigation, 
  AlertCircle, CheckCircle2, RefreshCw, Maximize, 
  ChevronLeft, ChevronRight, Thermometer
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DispatchDisplay = () => {
  const [weekData, setWeekData] = useState(null);
  const [weather, setWeather] = useState([]);
  const [travelTimes, setTravelTimes] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Format time from 24h to 12h
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Calculate "Leave By" time
  const calculateLeaveBy = (runTime, runDate, travelMinutes) => {
    if (!runTime || !travelMinutes) return null;
    
    const [hours, minutes] = runTime.split(':').map(Number);
    const [year, month, day] = runDate.split('-').map(Number);
    const runDateTime = new Date(year, month - 1, day, hours, minutes, 0);
    
    const leaveByTime = new Date(runDateTime.getTime() - (travelMinutes * 60 * 1000));
    const now = new Date();
    
    const minutesUntilLeave = differenceInMinutes(leaveByTime, now);
    
    return {
      time: format(leaveByTime, 'h:mm a'),
      minutesUntil: minutesUntilLeave,
      isPast: minutesUntilLeave < 0,
      isUrgent: minutesUntilLeave >= 0 && minutesUntilLeave <= 30,
      isSoon: minutesUntilLeave > 30 && minutesUntilLeave <= 60
    };
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [weekResponse, weatherResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dispatch/week-data?week_offset=${weekOffset}`),
        axios.get(`${BACKEND_URL}/api/dispatch/weather`)
      ]);
      
      setWeekData(weekResponse.data);
      setWeather(weatherResponse.data.forecast || []);
      setLastUpdated(new Date());
      
      // Fetch travel times for all runs with coordinates
      const allRuns = weekResponse.data.days.flatMap(day => day.trucking);
      const runsWithCoords = allRuns.filter(run => run.location_lat && run.location_lon);
      
      const travelTimePromises = runsWithCoords.map(async (run) => {
        const key = `${run.location_lat},${run.location_lon}`;
        if (travelTimes[key]) return { key, data: travelTimes[key] };
        
        try {
          const response = await axios.get(`${BACKEND_URL}/api/dispatch/travel-time?dest_lat=${run.location_lat}&dest_lon=${run.location_lon}`);
          return { key, data: response.data };
        } catch (e) {
          return { key, data: null };
        }
      });
      
      const travelTimeResults = await Promise.all(travelTimePromises);
      const newTravelTimes = { ...travelTimes };
      travelTimeResults.forEach(({ key, data }) => {
        if (data) newTravelTimes[key] = data;
      });
      setTravelTimes(newTravelTimes);
      
    } catch (error) {
      console.error('Failed to fetch dispatch data:', error);
    } finally {
      setLoading(false);
    }
  }, [weekOffset]);

  // Initial load and auto-refresh
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Get weather icon URL
  const getWeatherIcon = (icon) => {
    return `https://openweathermap.org/img/wn/${icon}@2x.png`;
  };

  // Get weather for a specific date
  const getWeatherForDate = (date) => {
    return weather.find(w => w.date === date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-xl">Loading Dispatch Display...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Truck className="h-8 w-8 text-orange-500" />
            <h1 className="text-2xl font-bold">AVFactory Dispatch</h1>
          </div>
          <div className="text-gray-400 text-sm">
            {weekData?.week_start && weekData?.week_end && (
              <span>{format(parseISO(weekData.week_start), 'MMM d')} - {format(parseISO(weekData.week_end), 'MMM d, yyyy')}</span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
            <button
              className="px-3 py-2 rounded-md bg-orange-500 text-white text-sm font-medium"
            >
              Week
            </button>
            <button
              onClick={() => window.location.href = '/dispatch/daily'}
              className="px-3 py-2 rounded-md hover:bg-gray-700 text-sm transition-colors"
            >
              Day
            </button>
          </div>
          
          {/* Week Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setWeekOffset(prev => prev - 1)}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => setWeekOffset(0)}
              className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm transition-colors"
            >
              This Week
            </button>
            <button
              onClick={() => setWeekOffset(prev => prev + 1)}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          {/* Current Time */}
          <div className="text-right">
            <div className="text-2xl font-mono text-orange-400">
              {format(currentTime, 'h:mm a')}
            </div>
            <div className="text-xs text-gray-500">
              {format(currentTime, 'EEEE, MMM d')}
            </div>
          </div>
          
          {/* Fullscreen & Last Updated */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              title="Toggle Fullscreen"
            >
              <Maximize className="h-5 w-5" />
            </button>
            {lastUpdated && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <RefreshCw className="h-3 w-3" />
                {format(lastUpdated, 'h:mm:ss a')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Office Address */}
      <div className="mb-4 text-sm text-gray-500 flex items-center gap-2">
        <Navigation className="h-4 w-4" />
        <span>From: {weekData?.office_address}</span>
      </div>

      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-2 h-[calc(100vh-140px)]">
        {weekData?.days.map((day) => {
          const dayWeather = getWeatherForDate(day.date);
          
          return (
            <div
              key={day.date}
              className={`flex flex-col rounded-xl overflow-hidden ${
                day.is_today 
                  ? 'bg-orange-900/30 ring-2 ring-orange-500' 
                  : 'bg-gray-900/50'
              }`}
            >
              {/* Day Header */}
              <div className={`p-3 ${day.is_today ? 'bg-orange-500/20' : 'bg-gray-800/50'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="text-xs text-gray-400 uppercase">{day.day_name}</div>
                    <div className="text-xl font-bold">{day.day_num}</div>
                  </div>
                  {dayWeather && (
                    <div className="flex items-center gap-1">
                      <img 
                        src={getWeatherIcon(dayWeather.icon)} 
                        alt={dayWeather.condition}
                        className="h-10 w-10 -my-2"
                      />
                      <div className="text-right">
                        <div className="text-sm font-medium">{Math.round(dayWeather.temp_high)}°</div>
                        <div className="text-xs text-gray-400">{Math.round(dayWeather.temp_low)}°</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-emerald-400">{day.confirmed_count} confirmed</span>
                  <span className="text-orange-400">{day.tentative_count} tentative</span>
                </div>
              </div>

              {/* Runs List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {day.trucking.length === 0 ? (
                  <div className="text-center text-gray-600 py-8">
                    <Truck className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No runs</p>
                  </div>
                ) : (
                  day.trucking.map((run) => {
                    const travelKey = `${run.location_lat},${run.location_lon}`;
                    const travelTime = travelTimes[travelKey];
                    const leaveBy = travelTime?.duration_minutes 
                      ? calculateLeaveBy(run.time, run.date, travelTime.duration_minutes)
                      : null;
                    const driver = run.truck_assignments?.[0];
                    
                    return (
                      <div
                        key={run.id}
                        className={`rounded-lg p-2.5 text-sm ${
                          run.status === 'Confirmed'
                            ? 'bg-emerald-900/30 border border-emerald-700/50'
                            : 'bg-gray-800/50 border border-gray-700/50'
                        }`}
                      >
                        {/* Time & Status */}
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-mono font-medium">
                              {formatTime(run.time)}
                            </span>
                          </div>
                          {run.status === 'Confirmed' ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-orange-400" />
                          )}
                        </div>

                        {/* Event Name */}
                        <div className="font-medium text-white mb-1 truncate" title={run.event_name}>
                          {run.event_name}
                          {run.flex_number && (
                            <span className="text-gray-400 ml-1">#{run.flex_number}</span>
                          )}
                        </div>

                        {/* Action */}
                        <div className="text-xs text-gray-400 mb-1.5">
                          {run.action}
                        </div>

                        {/* Location */}
                        <div className="flex items-start gap-1.5 mb-1.5">
                          <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-gray-300">
                            <div className="font-medium">{run.location_name}</div>
                            {run.location_address && (
                              <div className="text-gray-500">
                                {run.location_address}
                                {run.location_city && `, ${run.location_city}`}
                              </div>
                            )}
                            {run.dock_notes && (
                              <div className="text-orange-400/80">{run.dock_notes}</div>
                            )}
                          </div>
                        </div>

                        {/* Driver & Truck */}
                        {driver && (
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <User className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-xs">
                              <span className="text-emerald-400">{driver.driver_name}</span>
                              {driver.truck && (
                                <span className="text-gray-400"> • {driver.truck}</span>
                              )}
                            </span>
                          </div>
                        )}

                        {/* Travel Time & Leave By */}
                        {travelTime?.duration_minutes && (
                          <div className="mt-2 pt-2 border-t border-gray-700/50">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-500">
                                <Navigation className="h-3 w-3 inline mr-1" />
                                {travelTime.duration_minutes} min ({travelTime.distance_miles} mi)
                              </span>
                            </div>
                            {leaveBy && day.is_today && (
                              <div className={`mt-1 text-xs font-medium ${
                                leaveBy.isPast 
                                  ? 'text-red-400' 
                                  : leaveBy.isUrgent 
                                    ? 'text-red-400 animate-pulse' 
                                    : leaveBy.isSoon 
                                      ? 'text-yellow-400'
                                      : 'text-gray-400'
                              }`}>
                                {leaveBy.isPast ? (
                                  <span>Should have left at {leaveBy.time}</span>
                                ) : (
                                  <span>
                                    Leave by {leaveBy.time} 
                                    <span className="text-gray-500"> ({leaveBy.minutesUntil} min)</span>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DispatchDisplay;
